/**
 * <expandable-text>
 *
 * Clamps a paragraph to a few lines and adds a "Read more" toggle — the mobile
 * team header's history, which the frame cuts to three lines with a control
 * under it (Figma 1:9634).
 *
 * Two things it deliberately does not do:
 *
 *   1. It does not render the control in the markup. Without JavaScript the
 *      paragraph is not clamped, so a control would be a button that reveals
 *      text already on screen. The clamp and the control arrive together.
 *   2. It does not clamp at every width. The clamp is a CSS decision — the
 *      element only asks CSS whether one is in force, by reading the
 *      --expandable-lines custom property, and stays out of the way when it is
 *      not.
 */

if (!customElements.get('expandable-text')) {
  customElements.define(
    'expandable-text',
    class ExpandableText extends HTMLElement {
      constructor() {
        super();
        this.onResize = this.onResize.bind(this);
        this.onToggle = this.onToggle.bind(this);
      }

      connectedCallback() {
        this.body = this.querySelector('[data-expandable-body]');
        if (!this.body) return;

        this.expanded = false;
        this.sync();

        /* Whether the text overflows depends on the width it is laid out at, so
           a rotation or a resize can make the control necessary or pointless. */
        this.observer = new ResizeObserver(this.onResize);
        this.observer.observe(this);
      }

      disconnectedCallback() {
        this.observer?.disconnect();
        this.button?.removeEventListener('click', this.onToggle);
      }

      get clampLines() {
        const raw = getComputedStyle(this).getPropertyValue('--expandable-lines').trim();
        const lines = Number.parseInt(raw, 10);
        return Number.isFinite(lines) && lines > 0 ? lines : 0;
      }

      onResize() {
        if (this.expanded) return;
        this.sync();
      }

      /** Adds or removes the control based on whether the text actually overflows. */
      sync() {
        const clamped = this.clampLines > 0;
        this.toggleAttribute('data-clamped', clamped);

        if (!clamped) {
          this.removeButton();
          return;
        }

        const overflows = this.body.scrollHeight - this.body.clientHeight > 1;
        if (overflows) this.addButton();
        else this.removeButton();
      }

      addButton() {
        if (this.button) return;
        this.button = document.createElement('button');
        this.button.type = 'button';
        this.button.className = 'expandable-text__toggle';
        this.button.textContent = this.dataset.moreLabel || '';
        this.button.setAttribute('aria-expanded', 'false');
        this.button.setAttribute('aria-controls', this.body.id);
        this.button.addEventListener('click', this.onToggle);
        this.append(this.button);
      }

      removeButton() {
        if (!this.button) return;
        this.button.removeEventListener('click', this.onToggle);
        this.button.remove();
        this.button = null;
      }

      onToggle() {
        this.expanded = !this.expanded;
        this.toggleAttribute('data-expanded', this.expanded);
        this.button.setAttribute('aria-expanded', String(this.expanded));
        this.button.textContent = this.expanded
          ? this.dataset.lessLabel || ''
          : this.dataset.moreLabel || '';
      }
    },
  );
}
